import {
  ApplicationStatusEnum,
  BriefResponseDto,
  BriefStatusEnum,
  SocialChannelEnum,
  StatusHistory,
} from '@/app/common/general-types/briefs.dto';
import { summariseBriefAsk } from '@/app/common/brief-ask/summarise-brief-ask';
import { OwedPlacement, settleHire, SOCIAL_CHANNEL_ID } from '@/app/common/brief-pieces/piece-rules';
import { SocialPlacementChannel } from '@/app/common/general-types/briefs.dto';
import { VideoResponseDto, VideoStatusEnum } from '@/app/common/general-types/videos.dto';
import {
  BriefTrackerLookupResult,
  BriefTrackerStatusHistory,
  BriefTrackerStatusUser,
  BriefTrackerSubmittedPostChecklist,
  BriefTrackerSuperSocialAccount,
  BriefTrackerSuperSocialPlatform,
  BriefTrackerSuperSocialProfile,
  BriefTrackerVideoChecklist,
  BriefTrackerVideoItem,
} from './brief-tracker.types';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { SubmittedPostStatusEnum } from '@/app/common/general-types/submitted-posts.dto';
import { format, isValid, parseISO } from 'date-fns';
import { briefCarriesAdLicense } from '../../../../../common/brief-pieces/ad-license';

export function creatorFullName(creator: CreatorResponseDto | null): string {
  if (!creator) {
    return '—';
  }
  const name = [creator.firstName, creator.lastName].filter(Boolean).join(' ').trim();
  return name || creator.email || '—';
}

export function formatBriefTrackerDate(value: string | Date): string {
  const date = value instanceof Date ? value : parseISO(String(value));
  if (!isValid(date)) {
    return String(value);
  }
  return format(date, 'dd/MM/yyyy HH:mm');
}

export function briefVisibilityLabel(brief: BriefResponseDto | null): string {
  if (!brief) {
    return '—';
  }

  const status = brief.currentStatus?.status;
  if (status === BriefStatusEnum.Public) {
    return 'Public';
  }
  if (status === BriefStatusEnum.Private) {
    return 'Private';
  }
  if (status === BriefStatusEnum.Draft) {
    return 'Draft';
  }
  if (status === BriefStatusEnum.Complete) {
    return 'Complete';
  }

  return brief.isPublic ? 'Public' : 'Private';
}

export function socialPlatformLabel(platform: number | undefined | null): string {
  if (platform == null) {
    return '—';
  }
  return SocialChannelEnum[platform] ?? `Unknown (${platform})`;
}

const SOCIAL_PLATFORM_LOGOS = {
  tiktok: 'https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/socials/tiktok-logo.webp',
  instagram: 'https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/socials/instagram-logo.webp',
  youtube: 'https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/socials/youtube-logo.webp',
  snapchat: 'https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/socials/snapchat-logo.png',
} as const;

export interface ShoutBriefPlatformRow {
  id: number;
  label: string;
  isPrimary: boolean;
  logoUrl: string | null;
}

export function socialPlatformLogoUrl(platform: number | undefined | null): string | null {
  switch (Number(platform)) {
    case SocialChannelEnum['TikTok Post']:
      return SOCIAL_PLATFORM_LOGOS.tiktok;
    case SocialChannelEnum['Instagram Reel']:
    case SocialChannelEnum['Instagram Story']:
      return SOCIAL_PLATFORM_LOGOS.instagram;
    case SocialChannelEnum['YouTube Shorts']:
      return SOCIAL_PLATFORM_LOGOS.youtube;
    case SocialChannelEnum['Snapchat Spotlight']:
    case SocialChannelEnum['Snapchat Story']:
      return SOCIAL_PLATFORM_LOGOS.snapchat;
    default:
      return null;
  }
}

/** TypeORM `simple-array` may arrive as `number[]`, `string[]`, or `"0,2,3"`. */
export function normalizeCrossPlatformIds(raw: unknown): number[] {
  const values: unknown[] = Array.isArray(raw)
    ? raw
    : typeof raw === 'string' && raw.trim().length > 0
      ? raw.split(',')
      : [];

  const ids: number[] = [];
  const seen = new Set<number>();
  for (const value of values) {
    const parsed = Number(typeof value === 'string' ? value.trim() : value);
    if (!Number.isFinite(parsed) || seen.has(parsed) || parsed === SocialChannelEnum['Not Set']) {
      continue;
    }
    seen.add(parsed);
    ids.push(parsed);
  }
  return ids;
}

export function shoutBriefPlatformRows(shoutBrief: {
  socialPlatform?: number | null;
  crossPlatforms?: unknown;
} | null | undefined): ShoutBriefPlatformRow[] {
  if (!shoutBrief) {
    return [];
  }

  const rows: ShoutBriefPlatformRow[] = [];
  const seen = new Set<number>();
  const primary = shoutBrief.socialPlatform;
  if (primary != null && Number.isFinite(Number(primary))) {
    const primaryId = Number(primary);
    rows.push({
      id: primaryId,
      label: socialPlatformLabel(primaryId),
      isPrimary: true,
      logoUrl: socialPlatformLogoUrl(primaryId),
    });
    seen.add(primaryId);
  }

  for (const id of normalizeCrossPlatformIds(shoutBrief.crossPlatforms)) {
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    rows.push({
      id,
      label: socialPlatformLabel(id),
      isPrimary: false,
      logoUrl: socialPlatformLogoUrl(id),
    });
  }

  return rows;
}

export function shoutBriefPlatformLabels(shoutBrief: {
  socialPlatform?: number | null;
  crossPlatforms?: unknown;
} | null | undefined): string[] {
  return shoutBriefPlatformRows(shoutBrief).map((row) => row.label);
}

export function shoutBriefPlatformGroups(shoutBrief: {
  socialPlatform?: number | null;
  crossPlatforms?: unknown;
} | null | undefined): {
  primary: ShoutBriefPlatformRow | null;
  additional: ShoutBriefPlatformRow[];
} {
  const rows = shoutBriefPlatformRows(shoutBrief);
  return {
    primary: rows.find((row) => row.isPrimary) ?? null,
    additional: rows.filter((row) => !row.isPrimary),
  };
}

export function shoutBriefPlatformRowClass(isPrimary: boolean): string {
  return isPrimary
    ? 'flex items-center gap-2.5 rounded-lg border px-2.5 py-2 border-emerald-300/80 bg-emerald-50/90 dark:border-emerald-700/50 dark:bg-emerald-950/30'
    : 'flex items-center gap-2.5 rounded-lg border px-2.5 py-2 border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/60';
}

export function shoutBriefPlatformBadgeClass(isPrimary: boolean): string {
  return isPrimary
    ? 'm-0 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
    : 'm-0 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-zinc-500/10 text-secondary';
}

export function shoutBriefCrossPlatformsLabel(raw: unknown): string {
  const ids = normalizeCrossPlatformIds(raw);
  if (ids.length === 0) {
    return '—';
  }
  return ids.map((id) => `${socialPlatformLabel(id)} (${id})`).join(', ');
}

/** Mirrors manage-brief `getPostType()` — UGC Only, Ad Content, or social platform. */
export function briefTypeFromBrief(brief: BriefResponseDto | null): {
  label: string;
  detail: string | null;
} {
  if (!brief) {
    return { label: '—', detail: null };
  }

  const ask = summariseBriefAsk(brief);
  if (ask.source === 'pieces') {
    return { label: ask.label, detail: ask.detail };
  }

  const shoutBriefId = brief.shoutBriefId ?? brief.shoutBrief?.id;
  if (brief.shoutBrief || shoutBriefId) {
    const labels = shoutBriefPlatformLabels(brief.shoutBrief);
    return {
      label: 'Social media',
      detail: labels.length > 0 ? labels.join(' + ') : 'Social media',
    };
  }

  if (briefCarriesAdLicense(brief)) {
    return { label: 'Ad content', detail: null };
  }

  return { label: 'UGC', detail: null };
}

export function sortStatusHistoryAsc(history: StatusHistory[]): StatusHistory[] {
  return [...history].sort((a, b) => {
    const aTime = new Date(a.createdDate).getTime();
    const bTime = new Date(b.createdDate).getTime();
    return aTime - bTime;
  });
}

export function isStatusHistoryUserStatus(status: number): boolean {
  return status === ApplicationStatusEnum.Hired || status === ApplicationStatusEnum.HiredRemoved;
}

export function uniqueStatusHistoryUserIds(history: StatusHistory[]): string[] {
  const ids = new Set<string>();
  for (const row of history) {
    const userId = row.userId?.trim();
    if (isStatusHistoryUserStatus(row.status) && userId) {
      ids.add(userId);
    }
  }
  return [...ids];
}

export function attachStatusHistoryUsers(
  history: StatusHistory[],
  usersById: Record<string, BriefTrackerStatusUser | null>,
): BriefTrackerStatusHistory[] {
  return history.map((row) => {
    const userId = row.userId?.trim();
    if (!isStatusHistoryUserStatus(row.status) || !userId) {
      return row;
    }
    return {
      ...row,
      user: usersById[userId] ?? null,
    };
  });
}

export function statusHistoryUserLabel(row: BriefTrackerStatusHistory): string {
  const user = row.user;
  if (user) {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (name && user.email) {
      return `${name} (${user.email})`;
    }
    return name || user.email || row.userId?.trim() || 'Unknown user';
  }
  return row.userId?.trim() || 'Unknown user';
}

export function videoStatusLabel(status: number | undefined | null): string {
  if (status == null) {
    return '—';
  }
  return VideoStatusEnum[status] ?? `Unknown (${status})`;
}

type PieceHireApplication = {
  hireRecord?: { owed?: readonly OwedPlacement[] } | null;
} | null;

function owedPlacementsForBrief(
  brief: BriefResponseDto | null,
  application?: PieceHireApplication,
): OwedPlacement[] | null {
  const hired = application?.hireRecord?.owed;
  if (hired?.length) {
    return [...hired];
  }
  if (!brief?.pieces?.length) {
    return null;
  }
  const owed: OwedPlacement[] = [];
  for (const piece of brief.pieces) {
    for (const placement of piece.placements) {
      if (placement.kind === 'ugc') {
        owed.push({ pieceLabel: piece.label, kind: 'ugc' });
      } else {
        owed.push({ pieceLabel: piece.label, kind: 'social', channel: placement.channel });
      }
    }
  }
  return owed.length > 0 ? owed : null;
}

function pieceListPostChecklist(
  result: Pick<BriefTrackerLookupResult, 'brief' | 'submittedPosts'> &
    Partial<Pick<BriefTrackerLookupResult, 'videoChecklist'>>,
  owed: readonly OwedPlacement[],
): BriefTrackerSubmittedPostChecklist {
  const socialOwed = owed.filter((placement) => placement.kind === 'social');
  const videos = result.videoChecklist?.videos ?? [];
  if (socialOwed.length === 0) {
    return {
      applies: false,
      platform: null,
      platformLabel: 'UGC',
      videosPerCreator: 0,
      validPostCount: 0,
      submittedPostCountOnPlatform: 0,
      meetsTarget: true,
      shortfall: 0,
    };
  }

  const pieceByVideoId = new Map<string, string>();
  const uploads = videos.flatMap((video) => {
    if (!video.pieceLabel) {
      return [];
    }
    pieceByVideoId.set(video.id, video.pieceLabel);
    return [{ pieceLabel: video.pieceLabel, accepted: video.status === VideoStatusEnum.Accepted }];
  });
  const channelById = new Map<number, SocialPlacementChannel>(
    Object.entries(SOCIAL_CHANNEL_ID).map(([channel, id]) => [id, channel as SocialPlacementChannel]),
  );
  const posts = result.submittedPosts.flatMap((post) => {
    const pieceLabel = post.videoId ? pieceByVideoId.get(post.videoId) : undefined;
    const channel = channelById.get(Number(post.socialChannel));
    if (!pieceLabel || !channel) {
      return [];
    }
    return [{ pieceLabel, channel, valid: post.status === SubmittedPostStatusEnum.Valid }];
  });
  const settlement = settleHire({
    owed,
    uploads,
    posts,
    frozenAmount: 0,
  });
  const complete = settlement.placements.filter(
    (placement) => placement.kind === 'social' && placement.complete,
  ).length;

  return {
    applies: true,
    platform: null,
    platformLabel: 'owed placements',
    videosPerCreator: socialOwed.length,
    validPostCount: complete,
    submittedPostCountOnPlatform: posts.length,
    meetsTarget: complete === socialOwed.length,
    shortfall: Math.max(0, socialOwed.length - complete),
  };
}

function acceptedPieceLabels(videos: readonly { pieceLabel?: string | null; status?: number | null }[]): Set<string> {
  const labels = new Set<string>();
  for (const video of videos) {
    if (video.status === VideoStatusEnum.Accepted && video.pieceLabel) {
      labels.add(video.pieceLabel);
    }
  }
  return labels;
}

export function buildVideoChecklist(
  brief: BriefResponseDto | null,
  videos: VideoResponseDto[],
  application?: PieceHireApplication,
): BriefTrackerVideoChecklist {
  const mapped = videos.map((video) => ({
    id: video.id,
    videoName: video.videoName || '—',
    fullUrl: video.fullUrl || null,
    status: video.status,
    statusLabel: videoStatusLabel(video.status),
    pieceLabel: video.pieceLabel ?? null,
  }));
  const owed = owedPlacementsForBrief(brief, application);
  if (owed) {
    const labels = [...new Set(owed.map((placement) => placement.pieceLabel))];
    const accepted = acceptedPieceLabels(mapped);
    const videoCount = labels.filter((label) => accepted.has(label)).length;
    return {
      videosPerCreator: labels.length,
      videoCount,
      meetsTarget: labels.length > 0 && videoCount === labels.length,
      videos: mapped,
    };
  }

  const videosPerCreator = brief?.videosPerCreator ?? 0;
  const acceptedVideoCount = videos.filter(
    (video) => video.status === VideoStatusEnum.Accepted,
  ).length;

  return {
    videosPerCreator,
    videoCount: acceptedVideoCount,
    meetsTarget: acceptedVideoCount === videosPerCreator,
    videos: mapped,
  };
}

/** Posted in history but applicationStatus still ReadyToPost — completion check likely failed or not re-run. */
export function canRevalidateSubmittedPost(status: string | undefined | null): boolean {
  return (
    status != null &&
    status !== SubmittedPostStatusEnum.Valid &&
    status !== SubmittedPostStatusEnum.Deleted
  );
}

/** Application statuses where an admin may still force-validate a submitted post. */
const FORCE_VALIDATE_APPLICATION_STATUSES: ReadonlySet<number> = new Set([
  ApplicationStatusEnum.Started,
  ApplicationStatusEnum.ReadyToPost,
  ApplicationStatusEnum.Posted,
  ApplicationStatusEnum.Completed,
  ApplicationStatusEnum.Paid,
  ApplicationStatusEnum.BonusPaid,
]);

/**
 * Admin override: mark a non-Valid post as Valid.
 * Shown after Posted as well so extra-platform posts can still be forced through.
 */
const SUBMITTED_POST_STATUSES_THAT_FREE_THE_VIDEO: ReadonlySet<string> = new Set([
  SubmittedPostStatusEnum.Invalid,
  SubmittedPostStatusEnum.Duplicate,
  SubmittedPostStatusEnum.Deleted,
]);

const PRIMARY_CREATE_SUBMITTED_POST_STATUSES: ReadonlySet<number> = new Set([
  ApplicationStatusEnum.Started,
  ApplicationStatusEnum.ReadyToPost,
]);

/** Approved videos that do not already have a live submitted post on this channel. */
export function videosAvailableForSubmittedPost(
  videos: BriefTrackerVideoItem[],
  posts: { videoId: string; socialChannel: number; status: string }[],
  socialChannel: number,
): BriefTrackerVideoItem[] {
  const blockedVideoIds = new Set(
    posts
      .filter(
        (post) =>
          Number(post.socialChannel) === Number(socialChannel) &&
          !SUBMITTED_POST_STATUSES_THAT_FREE_THE_VIDEO.has(post.status),
      )
      .map((post) => post.videoId),
  );

  return videos.filter(
    (video) => video.status === VideoStatusEnum.Accepted && !blockedVideoIds.has(video.id),
  );
}

export function canCreateSubmittedPostOnChannel(
  applicationStatus: number | undefined | null,
  socialChannel: number,
  primaryPlatform: number | undefined | null,
): boolean {
  if (applicationStatus == null || primaryPlatform == null) {
    return false;
  }
  if (Number(socialChannel) === Number(primaryPlatform)) {
    return PRIMARY_CREATE_SUBMITTED_POST_STATUSES.has(applicationStatus);
  }
  return (
    PRIMARY_CREATE_SUBMITTED_POST_STATUSES.has(applicationStatus) ||
    applicationStatus === ApplicationStatusEnum.Posted ||
    applicationStatus === ApplicationStatusEnum.Completed
  );
}

export function canOfferCreateSubmittedPost(
  applicationStatus: number | undefined | null,
  shoutBrief: { socialPlatform?: number | null; crossPlatforms?: unknown } | null | undefined,
  videos: BriefTrackerVideoItem[],
  posts: { videoId: string; socialChannel: number; status: string }[],
): boolean {
  if (!shoutBrief) {
    return false;
  }
  return shoutBriefPlatformRows(shoutBrief).some(
    (row) =>
      canCreateSubmittedPostOnChannel(applicationStatus, row.id, shoutBrief.socialPlatform) &&
      videosAvailableForSubmittedPost(videos, posts, row.id).length > 0,
  );
}

export function canForceValidateSubmittedPost(
  postStatus: string | undefined | null,
  applicationStatus: number | undefined | null,
): boolean {
  return (
    canRevalidateSubmittedPost(postStatus) &&
    applicationStatus != null &&
    FORCE_VALIDATE_APPLICATION_STATUSES.has(applicationStatus)
  );
}

/** Brand users: Un-hire only while still Hired (before Started). */
const BRAND_UNHIREABLE_APPLICATION_STATUSES: ReadonlySet<number> = new Set([
  ApplicationStatusEnum.Hired,
]);

/** Shout Agency admins: Un-hire through Completed (not Paid / already removed). */
const ADMIN_UNHIREABLE_APPLICATION_STATUSES: ReadonlySet<number> = new Set([
  ApplicationStatusEnum.Hired,
  ApplicationStatusEnum.Started,
  ApplicationStatusEnum.ReadyToPost,
  ApplicationStatusEnum.Posted,
  ApplicationStatusEnum.Completed,
]);

export function canUnhireApplication(
  applicationStatus: number | undefined | null,
  isAdmin = false,
): boolean {
  if (applicationStatus == null) {
    return false;
  }
  const allowed = isAdmin
    ? ADMIN_UNHIREABLE_APPLICATION_STATUSES
    : BRAND_UNHIREABLE_APPLICATION_STATUSES;
  return allowed.has(applicationStatus);
}

/** Un-hired rows can be returned to Applied so the brand can hire again. */
export function canRestoreUnhiredToApplied(
  applicationStatus: number | undefined | null,
): boolean {
  return applicationStatus === ApplicationStatusEnum.HiredRemoved;
}

export function isSubmittedPostValidationPending(status: string | undefined | null): boolean {
  return (
    status === SubmittedPostStatusEnum.Processing ||
    status === SubmittedPostStatusEnum.Created ||
    status === SubmittedPostStatusEnum.Updated
  );
}

export function countValidSubmittedPostsForPlatform(
  posts: { status: string; socialChannel: number }[],
  platform: number | null | undefined,
): number {
  if (platform == null) {
    return posts.filter((post) => post.status === SubmittedPostStatusEnum.Valid).length;
  }
  return posts.filter(
    (post) => post.status === SubmittedPostStatusEnum.Valid && post.socialChannel === platform,
  ).length;
}

function countSubmittedPostsOnPlatform(
  posts: { status: string; socialChannel: number }[],
  platform: number | null | undefined,
): number {
  const active = posts.filter((post) => post.status !== SubmittedPostStatusEnum.Deleted);
  if (platform == null) {
    return active.length;
  }
  return active.filter((post) => post.socialChannel === platform).length;
}

/** Shout briefs: compare valid primary-platform posts to videosPerCreator (same gate as checkBriefComplete). */
export function buildSubmittedPostChecklist(
  result: Pick<BriefTrackerLookupResult, 'brief' | 'shoutBrief' | 'submittedPosts'> &
    Partial<Pick<BriefTrackerLookupResult, 'application' | 'videoChecklist'>>,
): BriefTrackerSubmittedPostChecklist {
  const owed = owedPlacementsForBrief(result.brief, result.application);
  if (owed) {
    return pieceListPostChecklist(result, owed);
  }

  const shoutBrief = result.shoutBrief;
  const videosPerCreator = result.brief?.videosPerCreator ?? 0;
  const platform = shoutBrief?.socialPlatform ?? null;
  const platformLabel = socialPlatformLabel(platform);

  if (!shoutBrief || videosPerCreator <= 0) {
    return {
      applies: false,
      platform,
      platformLabel,
      videosPerCreator,
      validPostCount: 0,
      submittedPostCountOnPlatform: 0,
      meetsTarget: true,
      shortfall: 0,
    };
  }

  const validPostCount = countValidSubmittedPostsForPlatform(result.submittedPosts, platform);
  const submittedPostCountOnPlatform = countSubmittedPostsOnPlatform(
    result.submittedPosts,
    platform,
  );
  const meetsTarget = validPostCount >= videosPerCreator;

  return {
    applies: true,
    platform,
    platformLabel,
    videosPerCreator,
    validPostCount,
    submittedPostCountOnPlatform,
    meetsTarget,
    shortfall: Math.max(0, videosPerCreator - validPostCount),
  };
}

export function submittedPostStatusPillClass(status: string | undefined | null): string {
  const base = 'pill';
  switch (status) {
    case SubmittedPostStatusEnum.Created:
    case SubmittedPostStatusEnum.Updated:
    case SubmittedPostStatusEnum.Processing:
      return `${base} pill-amber`;
    case SubmittedPostStatusEnum.Valid:
      return `${base} pill-green`;
    default:
      return `${base} pill-rose`;
  }
}

export function formatBriefTrackerNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return value.toLocaleString();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function asNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : null;
}

function asString(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const str = String(value).trim();
  return str.length ? str : null;
}

function asDateString(value: unknown): string | null {
  const str = asString(value);
  if (!str) {
    return null;
  }
  return formatBriefTrackerDate(str);
}

function asIsoDateString(value: unknown): string | null {
  const str = asString(value);
  if (!str) {
    return null;
  }
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? str : parsed.toISOString();
}

function hoursSinceDate(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return Math.floor((Date.now() - parsed.getTime()) / (1000 * 60 * 60));
}

/** Mirrors mgc-instagram / mgc-tiktok / mgc-youtube — refresh allowed if last update was 12+ hours ago. */
export function allowedToRefreshSuperSocialAccount(updatedDateIso: string | null | undefined): boolean {
  const hoursAgo = hoursSinceDate(updatedDateIso ?? null);
  if (hoursAgo == null) {
    return true;
  }
  return hoursAgo >= 12;
}

export function superSocialUpdatedDateColour(updatedDateIso: string | null | undefined): string {
  const hoursAgo = hoursSinceDate(updatedDateIso ?? null);
  if (hoursAgo == null) {
    return 'text-secondary';
  }
  if (hoursAgo < 12) {
    return 'text-green-500';
  }
  if (hoursAgo < 24) {
    return 'text-primary';
  }
  if (hoursAgo < 48) {
    return 'text-amber-500';
  }
  return 'text-red-500';
}

export function supportsSuperSocialAccountRefresh(
  platform: BriefTrackerSuperSocialPlatform,
): boolean {
  return platform === 'instagram' || platform === 'tiktok' || platform === 'youtube';
}

/** Stored handle/username for a SuperSocial account (Instagram uses username). */
export function superSocialAccountHandleValue(
  account: BriefTrackerSuperSocialAccount,
): string {
  if (account.platform === 'instagram') {
    return account.username ?? '';
  }
  return account.handle ?? '';
}

export function superSocialAccountIdentityLabel(
  platform: BriefTrackerSuperSocialPlatform,
): 'username' | 'handle' {
  return platform === 'instagram' ? 'username' : 'handle';
}

export function superSocialIdentityLabelForChannel(socialChannel: number): 'username' | 'handle' {
  const platform = socialChannelToSuperSocialPlatform(socialChannel);
  return platform ? superSocialAccountIdentityLabel(platform) : 'handle';
}

export function socialChannelToSuperSocialPlatform(
  socialChannel: number,
): BriefTrackerSuperSocialPlatform | null {
  switch (socialChannel) {
    case SocialChannelEnum['TikTok Post']:
      return 'tiktok';
    case SocialChannelEnum['Instagram Reel']:
    case SocialChannelEnum['Instagram Story']:
      return 'instagram';
    case SocialChannelEnum['YouTube Shorts']:
      return 'youtube';
    default:
      return null;
  }
}

/** Extract creator handle from a submitted post URL (fullUrl or videoLink). */
export function extractHandleFromPostUrl(
  url: string | null | undefined,
  socialChannel: number,
): string | null {
  const raw = asString(url);
  if (!raw) {
    return null;
  }

  const withoutQuery = raw.split('?')[0];

  if (socialChannel === SocialChannelEnum['TikTok Post']) {
    const match = withoutQuery.match(/tiktok\.com\/@([a-zA-Z0-9_.-]+)/i);
    return match?.[1] ?? null;
  }

  if (
    socialChannel === SocialChannelEnum['Instagram Reel'] ||
    socialChannel === SocialChannelEnum['Instagram Story']
  ) {
    const storyMatch = withoutQuery.match(/instagram\.com\/stories\/([^/]+)\//i);
    if (storyMatch) {
      return storyMatch[1];
    }

    const profilePostMatch = withoutQuery.match(
      /instagram\.com\/([a-zA-Z0-9._]+)\/(?:reel|reels|p)\//i,
    );
    if (profilePostMatch) {
      const candidate = profilePostMatch[1];
      const reserved = new Set(['reel', 'reels', 'p', 'stories', 'explore', 'tv']);
      if (!reserved.has(candidate.toLowerCase())) {
        return candidate;
      }
    }

    return null;
  }

  if (socialChannel === SocialChannelEnum['YouTube Shorts']) {
    const atMatch = withoutQuery.match(/youtube\.com\/@([a-zA-Z0-9._-]+)/i);
    if (atMatch) {
      return atMatch[1];
    }

    const legacyMatch = withoutQuery.match(/youtube\.com\/(?:c|user)\/([a-zA-Z0-9._-]+)/i);
    return legacyMatch?.[1] ?? null;
  }

  return null;
}

/** Prefer Meta-connected Instagram, then newest account, when several exist for one platform. */
export function pickPrimarySuperSocialAccount(
  accounts: BriefTrackerSuperSocialAccount[],
): BriefTrackerSuperSocialAccount | null {
  if (!accounts.length) {
    return null;
  }
  if (accounts.length === 1) {
    return accounts[0];
  }

  const instagramMeta = accounts.find(
    (account) =>
      account.platform === 'instagram' &&
      ['meta_api', 'meta'].includes((account.source ?? '').toLowerCase()),
  );
  if (instagramMeta) {
    return instagramMeta;
  }

  return accounts[0];
}

export function superSocialHandlesMatch(
  extractedHandle: string | null | undefined,
  superSocialHandle: string | null | undefined,
): boolean {
  const extracted = normalizeSuperSocialHandleInput(extractedHandle ?? '');
  const stored = normalizeSuperSocialHandleInput(superSocialHandle ?? '');
  if (!extracted.length || !stored.length) {
    return false;
  }
  return extracted.toLowerCase() === stored.toLowerCase();
}

export function findSuperSocialAccountsForPlatform(
  profile: BriefTrackerSuperSocialProfile,
  socialChannel: number,
): BriefTrackerSuperSocialAccount[] {
  const platform = socialChannelToSuperSocialPlatform(socialChannel);
  if (!platform) {
    return [];
  }
  return profile.accounts.filter((account) => account.platform === platform);
}

export function findSuperSocialAccountForPost(
  profile: BriefTrackerSuperSocialProfile,
  socialChannel: number,
): BriefTrackerSuperSocialAccount | null {
  return pickPrimarySuperSocialAccount(findSuperSocialAccountsForPlatform(profile, socialChannel));
}

/** Extract creator handle/username from submitted post URLs, then stored linkHandle. */
export function extractHandleFromSubmittedPost(post: {
  fullUrl?: string | null;
  videoLink?: string | null;
  linkHandle?: string | null;
  socialChannel: number;
}): string | null {
  for (const url of [post.fullUrl, post.videoLink]) {
    const extracted = extractHandleFromPostUrl(url, post.socialChannel);
    if (extracted) {
      return extracted;
    }
  }

  const linkHandle = normalizeSuperSocialHandleInput(post.linkHandle ?? '');
  return linkHandle || null;
}

export type SuperSocialHandleMatchStatus = 'match' | 'mismatch' | 'unknown';

export function submittedPostSuperSocialHandleMatch(
  post: {
    fullUrl?: string | null;
    videoLink?: string | null;
    linkHandle?: string | null;
    socialChannel: number;
  },
  profile: BriefTrackerSuperSocialProfile,
): {
  extractedHandle: string | null;
  superSocialHandle: string | null;
  matchedAccountId: string | null;
  status: SuperSocialHandleMatchStatus;
  matches: boolean;
} {
  const extractedHandle = extractHandleFromSubmittedPost(post);
  const platformAccounts = findSuperSocialAccountsForPlatform(profile, post.socialChannel);
  const matchingAccounts = extractedHandle
    ? platformAccounts.filter((account) =>
        superSocialHandlesMatch(extractedHandle, superSocialAccountHandleValue(account)),
      )
    : [];
  const primaryAccount = pickPrimarySuperSocialAccount(platformAccounts);
  const superSocialHandle = primaryAccount ? superSocialAccountHandleValue(primaryAccount) : null;

  let status: SuperSocialHandleMatchStatus = 'unknown';
  if (extractedHandle && platformAccounts.length) {
    status = matchingAccounts.length ? 'match' : 'mismatch';
  }

  return {
    extractedHandle,
    superSocialHandle,
    matchedAccountId: matchingAccounts[0]?.id ?? null,
    status,
    matches: status === 'match',
  };
}

export function normalizeSuperSocialHandleInput(value: string): string {
  return value.replace(/^@+/, '').trim();
}

export function buildSuperSocialHandleDrafts(
  accounts: BriefTrackerSuperSocialAccount[],
): Record<string, string> {
  const drafts: Record<string, string> = {};
  for (const account of accounts) {
    drafts[account.id] = superSocialAccountHandleValue(account);
  }
  return drafts;
}

export function isSuperSocialHandleDraftDirty(
  account: BriefTrackerSuperSocialAccount,
  draft: string | undefined,
): boolean {
  const current = superSocialAccountHandleValue(account);
  const normalizedDraft = normalizeSuperSocialHandleInput(draft ?? '');
  return normalizedDraft.length > 0 && normalizedDraft !== current;
}

export function applySuperSocialHandleUpdate(
  account: BriefTrackerSuperSocialAccount,
  handle: string,
): BriefTrackerSuperSocialAccount {
  if (account.platform === 'instagram') {
    return { ...account, username: handle };
  }
  return { ...account, handle };
}

/** True when SuperSocial reports a newer updatedDate for the account than before refresh. */
export function superSocialAccountWasUpdated(
  profile: BriefTrackerSuperSocialProfile,
  accountId: string,
  previousUpdatedDateIso: string | null | undefined,
): boolean {
  const account = profile.accounts.find((a) => a.id === accountId);
  if (!account?.updatedDateIso) {
    return false;
  }
  if (!previousUpdatedDateIso) {
    return true;
  }
  const previous = parseISO(previousUpdatedDateIso);
  const current = parseISO(account.updatedDateIso);
  if (!isValid(previous) || !isValid(current)) {
    return account.updatedDateIso !== previousUpdatedDateIso;
  }
  return current.getTime() > previous.getTime();
}

function firstAccount(accounts: unknown): Record<string, unknown> | null {
  if (!Array.isArray(accounts) || accounts.length === 0) {
    return null;
  }
  return asRecord(accounts[0]);
}

function allAccounts(accounts: unknown): Record<string, unknown>[] {
  if (!Array.isArray(accounts)) {
    return [];
  }
  return accounts
    .map((account) => asRecord(account))
    .filter((account): account is Record<string, unknown> => account != null);
}

function mapInstagramAccount(raw: Record<string, unknown>): BriefTrackerSuperSocialAccount {
  return {
    platform: 'instagram',
    platformLabel: 'Instagram',
    id: asString(raw['id']) ?? '—',
    createdDate: asDateString(raw['createdDate']),
    updatedDate: asDateString(raw['updatedDate']),
    updatedDateIso: asIsoDateString(raw['updatedDate']),
    source: asString(raw['source']),
    avatarUrl: asString(raw['profile_picture_url']),
    username: asString(raw['username']) ?? asString(raw['handle']),
    name: asString(raw['name']),
    followers_count: asNumber(raw['followers_count']),
    follows_count: asNumber(raw['follows_count']),
    media_count: asNumber(raw['media_count']),
    totalViews: asNumber(raw['totalViews']),
    totalLikes: asNumber(raw['totalLikes']),
    totalComments: asNumber(raw['totalComments']),
    totalShares: asNumber(raw['totalShares']),
    totalSaves: asNumber(raw['totalSaves']),
  };
}

function mapFbInstagramAccount(raw: Record<string, unknown>): BriefTrackerSuperSocialAccount {
  return {
    platform: 'instagram',
    platformLabel: 'Instagram (Meta)',
    id: asString(raw['id']) ?? '—',
    createdDate: asDateString(raw['createdDate']),
    updatedDate: asDateString(raw['updatedDate']),
    updatedDateIso: asIsoDateString(raw['updatedDate']),
    source: 'meta_api',
    avatarUrl: asString(raw['profile_picture_url']),
    username: asString(raw['username']),
    name: asString(raw['name']),
    followers_count: asNumber(raw['followers_count']),
    follows_count: asNumber(raw['follows_count']),
    media_count: asNumber(raw['media_count']),
    totalViews: null,
    totalLikes: null,
    totalComments: null,
    totalShares: null,
    totalSaves: null,
  };
}

function appendUniqueInstagramAccount(
  accounts: BriefTrackerSuperSocialAccount[],
  candidate: BriefTrackerSuperSocialAccount,
): void {
  const candidateUsername = normalizeSuperSocialHandleInput(superSocialAccountHandleValue(candidate));
  if (!candidateUsername) {
    accounts.push(candidate);
    return;
  }

  const duplicateIndex = accounts.findIndex(
    (account) =>
      account.platform === 'instagram' &&
      superSocialHandlesMatch(superSocialAccountHandleValue(account), candidateUsername),
  );
  if (duplicateIndex === -1) {
    accounts.push(candidate);
    return;
  }

  const existing = accounts[duplicateIndex];
  const preferCandidate =
    (candidate.source ?? '').toLowerCase() === 'meta_api' &&
    (existing.source ?? '').toLowerCase() !== 'meta_api';
  if (preferCandidate) {
    accounts[duplicateIndex] = candidate;
  }
}

function mapTiktokAccount(raw: Record<string, unknown>): BriefTrackerSuperSocialAccount {
  return {
    platform: 'tiktok',
    platformLabel: 'TikTok',
    id: asString(raw['id']) ?? '—',
    createdDate: asDateString(raw['createdDate']),
    updatedDate: asDateString(raw['updatedDate']),
    updatedDateIso: asIsoDateString(raw['updatedDate']),
    source: asString(raw['source']),
    avatarUrl: asString(raw['avatarUrl']),
    handle: asString(raw['handle']),
    displayName: asString(raw['displayName']),
    followers_count: asNumber(raw['followerCount']),
    follows_count: asNumber(raw['followingCount']),
    media_count: asNumber(raw['videoCount']),
    totalViews: asNumber(raw['totalViews']),
    totalLikes: asNumber(raw['totalLikes']),
    totalComments: asNumber(raw['totalComments']),
    totalShares: asNumber(raw['totalShares']),
    totalSaves: asNumber(raw['totalSaves']),
  };
}

function mapYoutubeAccount(raw: Record<string, unknown>): BriefTrackerSuperSocialAccount {
  return {
    platform: 'youtube',
    platformLabel: 'YouTube',
    id: asString(raw['id']) ?? '—',
    createdDate: asDateString(raw['createdDate']),
    updatedDate: asDateString(raw['updatedDate']),
    updatedDateIso: asIsoDateString(raw['updatedDate']),
    source: asString(raw['source']),
    avatarUrl: asString(raw['thumbnail_url']),
    handle: asString(raw['handle']),
    title: asString(raw['title']),
    channel_id: asString(raw['channel_id']),
    subscriber_count: asNumber(raw['subscriber_count']),
    view_count: asNumber(raw['view_count']),
    video_count: asNumber(raw['video_count']),
    total_likes: asNumber(raw['total_likes']),
    total_comments: asNumber(raw['total_comments']),
  };
}

export function buildSuperSocialProfile(raw: unknown): BriefTrackerSuperSocialProfile {
  const creator = asRecord(raw);
  if (!creator) {
    return {
      found: false,
      superSocialCreatorId: null,
      externalId: null,
      accounts: [],
    };
  }

  const accounts: BriefTrackerSuperSocialAccount[] = [];

  for (const raw of allAccounts(creator['fbInstagramAccounts'])) {
    appendUniqueInstagramAccount(accounts, mapFbInstagramAccount(raw));
  }

  for (const raw of allAccounts(creator['instagramAccounts'])) {
    appendUniqueInstagramAccount(accounts, mapInstagramAccount(raw));
  }

  const tiktok = firstAccount(creator['tiktokAccounts']);
  const youtube = firstAccount(creator['youtubeAccounts']);

  if (tiktok) {
    accounts.push(mapTiktokAccount(tiktok));
  }
  if (youtube) {
    accounts.push(mapYoutubeAccount(youtube));
  }

  return {
    found: true,
    superSocialCreatorId: asString(creator['id']),
    externalId: asString(creator['externalId']),
    accounts,
  };
}

export function emptySuperSocialProfile(): BriefTrackerSuperSocialProfile {
  return {
    found: false,
    superSocialCreatorId: null,
    externalId: null,
    accounts: [],
  };
}

export function shouldRunCompletionCheck(result: BriefTrackerLookupResult): boolean {
  const application = result.application;
  if (!application?.id) {
    return false;
  }
  if (application.applicationStatus !== ApplicationStatusEnum.ReadyToPost) {
    return false;
  }
  return result.statusHistory.some((row) => row.status === ApplicationStatusEnum.Posted);
}
